import {Controller} from "stimulus"
import AutoNumeric from "autonumeric"

export var DEBOUNCE_DELAY = 400;

export var INPUT_FIELD_CLASSES = 'm-0 text-[16px] p-3 w-full rounded-xl border-2 border-gray-300 focus-visible:border-purple-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600';

export var PRICING_MESSAGE = 'Calculating price…';
export var PRICING_BLOCKED_MESSAGE = 'Wait for the price to finish calculating.';
export var PRICING_FAILED_MESSAGE = 'The price could not be calculated. Enter the charge yourself.';

export function collectInputValues(container) {
    var inputFields = container.querySelectorAll('.misc-charge-dynamic-fields input, .misc-charge-dynamic-fields select');
    var inputValues = {};
    inputFields.forEach(function(field) {
        var name = field.name.match(/\[input_values\]\[(\w+)\]/);
        if (name) inputValues[name[1]] = field.value;
    });
    return inputValues;
}

export function allChargesReady({clearFlash = true} = {}) {
    var allPostFieldValues = Array.prototype.map.call(
        document.querySelectorAll(".post-ready"),
        function(element) { return element.value; }
    );
    var allReady = allPostFieldValues.every(function(v) { return v.length > 0; });
    if (allReady && clearFlash) {
        var flashArea = document.querySelector('#flash-area');
        if (flashArea) flashArea.innerHTML = "";
    }
    document.querySelectorAll(".charge-checkout-button").forEach(function(btn) {
        btn.disabled = !allReady;
    });
    return allReady;
}

export default class extends Controller {

    static targets = [
        "inputFields", "amountField", "amountHint", "amountStatus", "feeBreakdown", "feeBreakdownList",
        "entitySearch", "entityResults", "entitySelected", "entitySelectedName", "entitySelectedType",
        "linkedEntityId", "linkedEntityType",
        "newContact", "newContactOpen", "newContactReplaces",
        "newContactFirstName", "newContactLastName"
    ]

    connect() {
        var amountEl = this.element.querySelector(".cr-adj-amount");
        if (amountEl) {
            this.chargeField = new AutoNumeric(
                amountEl,
                ['NorthAmerican', {unformatOnSubmit: true}]
            );
        }

        // Render input fields if a charge type is already selected on page load
        var select = this.element.querySelector("#cr_misc_charge_cr_charge_type_id");
        if (select && select.value) {
            this._renderInputFields(select);
            // A restricted user's form opens on their default charge type, and
            // with only one permitted type there is no change to wait for. An
            // amount already there -- an edit, a re-rendered save -- is kept.
            if (this.amountFieldTarget.value.trim() === '') this._doRecalculate();
        }

        this.searchGeneration = 0;

        // The results dropdown is not focusable, so a click elsewhere on the
        // page is the only signal that the user is done with it.
        this._dismissEntityResults = this._dismissEntityResults.bind(this);
        document.addEventListener('click', this._dismissEntityResults);

        // The server keeps a posted amount, so a submit that beats a price
        // lookup would save the old price with the new inputs. Enter in a
        // field submits too, so the form is guarded, not just its buttons.
        this._blockSubmitWhilePricing = this._blockSubmitWhilePricing.bind(this);
        this.element.addEventListener('submit', this._blockSubmitWhilePricing);

        // The checkout buttons render disabled, and a form the server
        // re-rendered after a rejected save can arrive already complete, so
        // readiness is worked out here too. The flash stays: on a re-render it
        // is the reason the save failed, not a stale readiness warning.
        this._updatePostReady({clearFlash: false});
    }

    disconnect() {
        document.removeEventListener('click', this._dismissEntityResults);
        this.element.removeEventListener('submit', this._blockSubmitWhilePricing);
    }

    addCharge(e) {
    }

    addToCart(e) {
    }

    chargeTypeChanged(e) {
        // Clear amount and reset readiness when switching charge type
        if (this.chargeField) {
            this.chargeField.set('');
        } else {
            this.amountFieldTarget.value = '';
        }
        this.amountFieldTarget.readOnly = false;
        this.amountFieldTarget.classList.remove('bg-gray-100');
        this._showFees([], {editable: false});
        this._announceAmount('');
        // A price still in flight was for the charge type the user just left,
        // even if they come back to it before it lands -- and so was a lookup
        // still waiting out the debounce on the inputs just replaced.
        clearTimeout(this._recalcTimer);
        this.calculationGeneration = (this.calculationGeneration || 0) + 1;
        this._setPricing(false);
        this._renderInputFields(e.target);
        this._updatePostReady();
        this._doRecalculate();
    }

    // A charge type is priced as soon as it is picked, before any input field
    // is filled in, so fees that don't depend on an input -- a flat fee, the
    // default of a choice left blank -- apply from the start (project #1413).
    // _doRecalculate skips one with no application fee. The price it fills in
    // stays editable: the server keeps whatever amount is posted
    // (CrMiscCharge#calculate_amount_from_inputs), and staff may override it.
    _hasNoInputFields(select) {
        if (!select.value) return false;
        var inputFieldsData = JSON.parse(select.dataset.crMiscChargesInputFieldsValue || '{}');
        var fields = inputFieldsData[select.value] || [];
        return fields.length === 0;
    }

    _renderInputFields(select) {
        var chargeTypeId = select.value;
        var inputFieldsData = JSON.parse(select.dataset.crMiscChargesInputFieldsValue || '{}');

        var inputFieldsContainer = this.inputFieldsTarget;
        var existingValues = JSON.parse(inputFieldsContainer.dataset.crMiscChargesExistingInputValues || '{}');
        inputFieldsContainer.innerHTML = '';

        if (chargeTypeId && inputFieldsData[chargeTypeId] && inputFieldsData[chargeTypeId].length > 0) {
            var fields = inputFieldsData[chargeTypeId];
            var wrapper = document.createElement('div');
            wrapper.className = 'misc-charge-dynamic-fields grid grid-cols-1 md:grid-cols-2 gap-4';
            var controller = this;

            fields.forEach(function(field) {
                var fieldDiv = document.createElement('div');

                var label = document.createElement('label');
                label.setAttribute('for', 'cr_misc_charge_input_values_' + field.name);
                label.className = 'block text-gray-500 mb-2';
                label.textContent = field.label;
                fieldDiv.appendChild(label);

                var value = field.name in existingValues ? existingValues[field.name] : null;
                if (field.field_type === 'select') {
                    fieldDiv.appendChild(controller._buildChoiceSelect(field, value));
                } else {
                    fieldDiv.appendChild(controller._buildInput(field, value));
                }

                wrapper.appendChild(fieldDiv);
            });

            inputFieldsContainer.appendChild(wrapper);
            inputFieldsContainer.style.display = '';
            // Clear existing values after first render so charge type
            // changes don't carry over stale data
            inputFieldsContainer.dataset.crMiscChargesExistingInputValues = '{}';

            // The amount stays open to staff: a calculated fee is only filled
            // in (CrMiscCharge#calculate_amount_from_inputs keeps what is posted).
            this.amountFieldTarget.readOnly = false;
            this.amountFieldTarget.classList.remove('bg-gray-100');
        } else {
            inputFieldsContainer.style.display = 'none';
            this.amountFieldTarget.readOnly = false;
            this.amountFieldTarget.classList.remove('bg-gray-100');
        }
    }

    _fieldName(field) {
        return 'cr_misc_charge[input_values][' + field.name + ']';
    }

    _fieldId(field) {
        return 'cr_misc_charge_input_values_' + field.name;
    }

    _buildInput(field, value) {
        var controller = this;
        var input = document.createElement('input');
        input.type = field.field_type === 'number' || field.field_type === 'date' ? field.field_type : 'text';
        input.name = this._fieldName(field);
        input.id = this._fieldId(field);
        input.className = INPUT_FIELD_CLASSES + ' shadow-inner shadow-gray-100 placeholder-gray-300';
        if (input.type === 'number') input.step = 'any';
        if (value !== null) input.value = value;
        // Bind directly — Stimulus 1.1 MutationObserver may not
        // pick up data-action on dynamically created elements.
        input.addEventListener('input', function() {
            controller.recalculate();
        });
        return input;
    }

    // A select or radio input field: one choice, picked from a dropdown. The
    // blank first option keeps it optional -- staff leave it blank to enter
    // the amount themselves. A picked choice is priced straight away.
    _buildChoiceSelect(field, value) {
        var controller = this;
        var wrapper = document.createElement('div');
        wrapper.className = 'grid grid-cols-[1fr_2rem]';

        var select = document.createElement('select');
        select.name = this._fieldName(field);
        select.id = this._fieldId(field);
        select.className = INPUT_FIELD_CLASSES + ' col-span-full row-start-1 appearance-none bg-white pr-8';

        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = 'Select ' + field.label;
        select.appendChild(blank);
        (field.choices || []).forEach(function(choice) {
            var option = document.createElement('option');
            option.value = choice;
            option.textContent = choice;
            select.appendChild(option);
        });
        // A saved choice no longer offered is kept, so an edit doesn't drop it.
        if (value !== null && value !== '' && (field.choices || []).indexOf(value) === -1) {
            var saved = document.createElement('option');
            saved.value = value;
            saved.textContent = value;
            select.appendChild(saved);
        }
        if (value !== null) select.value = value;

        select.addEventListener('change', function() {
            clearTimeout(controller._recalcTimer);
            controller._doRecalculate();
        });
        wrapper.appendChild(select);
        wrapper.insertAdjacentHTML('beforeend',
            '<svg viewBox="0 0 8 5" width="8" height="5" fill="none" aria-hidden="true"' +
            ' class="pointer-events-none col-start-2 row-start-1 place-self-center text-gray-500">' +
            '<path d="M.5.5 4 4 7.5.5" stroke="currentColor" /></svg>');
        return wrapper;
    }

    // amountAtStart is the amount when the inputs last changed: one typed
    // after that, even while a lookup waits out the debounce, is an override.
    _doRecalculate({amountAtStart = this._amountNumber()} = {}) {
        var formArea = this.element;
        var selectEl = formArea.querySelector("#cr_misc_charge_cr_charge_type_id");
        var chargeTypeId = selectEl.value;
        if (!chargeTypeId) {
            this._setPricing(false);
            return;
        }

        // Only call calculate when the charge type has an application fee
        // (CrChargeType#charges_application_fee?), the only fees a misc charge
        // is priced from. Charge types with input fields but no such fee use inputs for
        // informational capture — the user enters the amount manually.
        if (!this._isCalculable(selectEl)) {
            this._setPricing(false);
            this._updatePostReady();
            return;
        }

        var inputValues = collectInputValues(formArea);
        // With every input field blank the fees that don't depend on one are
        // still priced. Not every calculator can read a blank input, though,
        // and staff who filled nothing in asked for no price, so a lookup
        // that fails then leaves the amount to them without saying so.
        var nothingFilledIn = !this._hasNoInputFields(selectEl) && !this._anyInputFieldFilled(inputValues);
        // Only the newest request may touch the amount: an older one for the
        // same charge type can land after it, in success or in failure.
        var generation = this.calculationGeneration = (this.calculationGeneration || 0) + 1;
        var controller = this;
        var amountField = this.amountFieldTarget;
        // A calculated fee is only a prefill: an amount staff type while it is
        // in flight is an override, and the server keeps it (project #1413).
        // The number is compared, not the text: AutoNumeric writes its
        // currency symbol into an empty field on focus and on mouseenter.
        var overridden = function() {
            return controller._amountNumber() !== amountAtStart;
        };
        var isStale = function() {
            return controller.calculationGeneration !== generation || selectEl.value !== chargeTypeId || overridden();
        };

        var csrfMeta = document.querySelector('meta[name="csrf-token"]');
        var chargeField = this.chargeField;
        this._setPricing(true);

        fetch('/cr_misc_charges/calculate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfMeta ? csrfMeta.content : ''
            },
            body: JSON.stringify({cr_charge_type_id: chargeTypeId, input_values: inputValues})
        })
        .then(function(response) {
            // Priced for a charge type the user has since changed away from,
            // or overtaken by a newer request.
            if (isStale()) return null;
            if (!response.ok) {
                controller._pricingFailed({keepAmount: nothingFilledIn});
                return null;
            }
            return response.json();
        })
        .then(function(data) {
            if (!data || isStale()) return;
            var amt = parseFloat(data.amount);
            if (!isNaN(amt) && amt > 0) {
                if (chargeField) {
                    chargeField.set(data.amount);
                } else {
                    amountField.value = data.amount;
                }
                // Staff may override the fee filled in, whether flat or priced
                // from input fields; changing an input fills in a new one.
                amountField.readOnly = false;
                amountField.classList.remove('bg-gray-100');
                controller._showFees(data.fees || [], {editable: true});
                controller._announceAmount('Charge set to ' + amountField.value + '.');
            } else {
                controller._showFees([], {editable: false});
                if (chargeField) {
                    chargeField.set('');
                } else {
                    amountField.value = '';
                }
                amountField.readOnly = false;
                amountField.classList.remove('bg-gray-100');
            }
            controller._updatePostReady();
        })
        .catch(function() {
            if (isStale()) return;
            controller._pricingFailed({keepAmount: nothingFilledIn});
        })
        .then(function() {
            // Only the newest lookup settles pricing: an older one landing
            // first leaves the form blocked for the price still to come.
            if (controller.calculationGeneration === generation) {
                controller._setPricing(false);
                controller._updatePostReady();
            }
        });
    }

    // A failed lookup leaves the amount to staff. It is cleared and the
    // failure announced, unless keepAmount: then nothing was asked of it.
    _pricingFailed({keepAmount}) {
        this._showFees([], {editable: false});
        if (!keepAmount) {
            this._announceAmount(PRICING_FAILED_MESSAGE);
            if (this.chargeField) {
                this.chargeField.set('');
            } else {
                this.amountFieldTarget.value = '';
            }
        }
        this.amountFieldTarget.readOnly = false;
        this.amountFieldTarget.classList.remove('bg-gray-100');
        this._updatePostReady();
    }

    // While a price is being looked up -- waiting out the debounce or in
    // flight -- the form cannot be submitted. Submit buttons this disables are
    // remembered, so one disabled for another reason stays disabled after.
    _setPricing(pricing) {
        if (pricing === !!this._pricing) return;
        this._pricing = pricing;
        if (pricing) {
            this._pricingDisabled = Array.prototype.filter.call(
                this.element.querySelectorAll('input[type="submit"], button[type="submit"]'),
                function(button) { return !button.disabled; }
            );
            this._pricingDisabled.forEach(function(button) { button.disabled = true; });
            // The fees shown were for the inputs just changed; a lookup an
            // override discards would otherwise leave them up indefinitely.
            this._showFees([], {editable: false});
            this._announceAmount(PRICING_MESSAGE);
            this._updatePostReady();
        } else {
            (this._pricingDisabled || []).forEach(function(button) { button.disabled = false; });
            this._pricingDisabled = [];
            // A lookup that set the charge, or failed, has said so already.
            if (this._amountStatusText() === PRICING_MESSAGE || this._amountStatusText() === PRICING_BLOCKED_MESSAGE) {
                this._announceAmount('');
            }
        }
    }

    _blockSubmitWhilePricing(e) {
        if (!this._pricing) return;
        e.preventDefault();
        this._announceAmount(PRICING_BLOCKED_MESSAGE);
    }

    // Says, in a role="status" region under the amount, what is happening to
    // it: being priced, set to a fee, or left to staff after a failed lookup.
    _announceAmount(message) {
        if (this.hasAmountStatusTarget) this.amountStatusTarget.textContent = message;
    }

    _amountStatusText() {
        return this.hasAmountStatusTarget ? this.amountStatusTarget.textContent : '';
    }

    // Whether a lookup will go out for the form as it stands: a charge type
    // is picked and it is priced.
    _willPrice(selectEl) {
        return !!selectEl.value && this._isCalculable(selectEl);
    }

    _isCalculable(selectEl) {
        var calculableIds = JSON.parse(selectEl.dataset.crMiscChargesCalculableValue || '[]');
        return calculableIds.indexOf(parseInt(selectEl.value)) !== -1;
    }

    // The amount field's text as a number, ignoring formatting; blank is 0.
    _amountNumber() {
        return parseFloat(this.amountFieldTarget.value.replace(/[^0-9.\-]/g, '')) || 0;
    }

    // Lists the fees a calculated amount is made of, and says when the
    // amount filled in may be changed. Built with textContent: fee names are
    // typed by admins.
    _showFees(fees, {editable}) {
        if (this.hasAmountHintTarget) {
            this.amountHintTarget.classList.toggle('hidden', !editable);
        }
        if (!this.hasFeeBreakdownTarget || !this.hasFeeBreakdownListTarget) return;

        var list = this.feeBreakdownListTarget;
        list.innerHTML = '';
        fees.forEach(function(fee) {
            var item = document.createElement('li');
            item.className = 'flex justify-between gap-3';
            var name = document.createElement('span');
            name.textContent = fee.name;
            var amount = document.createElement('span');
            amount.className = 'tabular-nums';
            amount.textContent = fee.amount;
            item.appendChild(name);
            item.appendChild(amount);
            list.appendChild(item);
        });
        this.feeBreakdownTarget.classList.toggle('hidden', fees.length === 0);
        this._describeAmount({hint: editable, breakdown: fees.length > 0});
    }

    // Points the amount field at only the hint and breakdown now shown: a
    // screen reader reads hidden text that aria-describedby names directly.
    _describeAmount({hint, breakdown}) {
        if (!this.hasAmountFieldTarget) return;
        var ids = [];
        if (hint && this.hasAmountHintTarget) ids.push(this.amountHintTarget.id);
        if (breakdown) ids.push(this.feeBreakdownTarget.id);
        if (ids.length > 0) {
            this.amountFieldTarget.setAttribute('aria-describedby', ids.join(' '));
        } else {
            this.amountFieldTarget.removeAttribute('aria-describedby');
        }
    }

    recalculate(e) {
        var controller = this;
        // A price still in flight was for the inputs just changed: retire it
        // now, not when the debounced lookup finally goes out.
        controller.calculationGeneration = (controller.calculationGeneration || 0) + 1;
        // Typing that will never be priced must not announce or block a lookup.
        var selectEl = controller.element.querySelector("#cr_misc_charge_cr_charge_type_id");
        controller._setPricing(controller._willPrice(selectEl));
        var amountAtStart = controller._amountNumber();
        clearTimeout(controller._recalcTimer);
        controller._recalcTimer = setTimeout(function() {
            controller._doRecalculate({amountAtStart: amountAtStart});
        }, DEBOUNCE_DELAY);
    }

    _updatePostReady({clearFlash = true} = {}) {
        var formArea = this.element;
        var miscTypePresent = formArea.querySelector("#cr_misc_charge_cr_charge_type_id").value;
        var amountEl = formArea.querySelector("#cr_misc_charge_amount");
        var miscAmountValue = this.chargeField ? AutoNumeric.getNumber(amountEl) : parseFloat(amountEl.value);
        var miscAmountPresent = miscAmountValue > 0;
        var miscMemoPresent = formArea.querySelector("#cr_misc_charge_description").value;

        // Memo is optional when input fields are present and filled in
        var inputFieldsFilled = this._inputFieldsFilled();
        var descriptionOk = miscMemoPresent || inputFieldsFilled;

        var miscPostReadyField = formArea.querySelector(".post-ready");
        if (miscPostReadyField) {
            miscPostReadyField.value = (descriptionOk && miscAmountPresent && miscTypePresent && !this._pricing) ? "true" : "";
        }
        allChargesReady({clearFlash: clearFlash});
    }

    _anyInputFieldFilled(inputValues) {
        return Object.keys(inputValues).some(function(key) {
            return String(inputValues[key]).trim().length > 0;
        });
    }

    _inputFieldsFilled() {
        var container = this.inputFieldsTarget;
        var inputs = container.querySelectorAll('.misc-charge-dynamic-fields input, .misc-charge-dynamic-fields select');
        if (inputs.length === 0) return false;
        return Array.prototype.every.call(inputs, function(field) {
            return field.value.trim().length > 0;
        });
    }

    // Bound to `input`, not `keyup`: the native clear button on a search field
    // fires `input` and no key event at all, so a keyup binding leaves the
    // dropdown open after the user clears the box with the X.
    searchEntity(e) {
        clearTimeout(this.searchTimeout);
        const query = this.entitySearchTarget.value.trim();

        if (query.length === 0) {
            this.hideEntityResults();
            return;
        }

        // One- and two-character surnames are real -- "Li", "Ng" -- but too
        // short to search on. The panel still opens for them, carrying only
        // the quick-add action, so those payers stay reachable.
        if (query.length < 3) {
            this.showQuickAddOnly();
            return;
        }

        // A response that lands after the user dismissed the dropdown must not
        // reopen it, and neither must one overtaken by a later search. Claiming
        // a generation here rather than reading the current one is what makes
        // the second case work: two searches in flight hold different numbers.
        const generation = ++this.searchGeneration;
        this.searchTimeout = setTimeout(() => {
            fetch('/search/query.json?search=' + encodeURIComponent(query) + '&polymorphic=Business,Contact&limit=5')
                .then(response => response.json())
                .then(results => {
                    if (this.searchGeneration !== generation) return;
                    this.renderEntityResults(results);
                })
                .catch(error => console.error('Entity search error:', error));
        }, 300);
    }

    entitySearchKeydown(e) {
        if (e.key !== 'Escape') return;
        // Escape clears an `<input type="search">` in several browsers.
        // Dismissing the dropdown must not throw away the typed query.
        e.preventDefault();
        this.hideEntityResults();
    }

    // Dismissal has to retire the search in flight as well as the panel: a
    // debounced request still pending, or a response still on the wire, would
    // otherwise reopen the dropdown the user just closed.
    hideEntityResults() {
        clearTimeout(this.searchTimeout);
        this.searchGeneration += 1;
        this.entityResultsTarget.classList.add('hidden');
    }

    // No early return when the panel is already hidden — a click during the
    // debounce window has to cancel the pending search too.
    _dismissEntityResults(e) {
        if (!this.hasEntityResultsTarget) return;
        if (this.entityResultsTarget.contains(e.target)) return;
        if (this.hasEntitySearchTarget && this.entitySearchTarget.contains(e.target)) return;

        this.hideEntityResults();
    }

    renderEntityResults(results) {
        const typeMap = {businesses: 'Business', contacts: 'Contact'};
        const allowedTypes = Object.keys(typeMap);
        const container = this.entityResultsTarget;
        container.innerHTML = '';

        let hasResults = false;
        allowedTypes.forEach(type => {
            const items = results[type];
            if (items && items.length > 0) {
                hasResults = true;

                const header = document.createElement('div');
                header.className = 'px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100';
                header.textContent = typeMap[type] || type;
                container.appendChild(header);

                items.forEach(item => {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'block w-full text-left !text-black bg-purple-200 px-4 py-2 hover:bg-purple-700 cursor-pointer text-sm !p-2 !m-0 !rounded-none';
                    btn.textContent = this.entityDisplayName(type, item);
                    btn.dataset.entityId = item.id;
                    btn.dataset.entityType = typeMap[type] || type;
                    // Bind directly — Stimulus 1.1 MutationObserver may not
                    // pick up data-action on dynamically created elements.
                    btn.addEventListener('click', (e) => {
                        this.selectEntity(e);
                    });
                    container.appendChild(btn);
                });
            }
        });

        if (!hasResults) {
            const empty = document.createElement('div');
            empty.className = 'px-4 py-2 text-sm text-gray-400';
            empty.textContent = 'No results found';
            container.appendChild(empty);
        }

        container.appendChild(this.newContactTrigger({leading: false}));
        container.classList.remove('hidden');
    }

    // Reopens the panel with nothing in it but the quick-add action. Going
    // through hideEntityResults first retires any search still in flight, so
    // a slower response for a longer query cannot overwrite this.
    showQuickAddOnly() {
        this.hideEntityResults();

        const container = this.entityResultsTarget;
        container.innerHTML = '';
        container.appendChild(this.newContactTrigger({leading: true}));
        container.classList.remove('hidden');
    }

    // Always offered, results or not — the payer staff are looking for is
    // often one who has never been billed before.
    newContactTrigger({leading = false} = {}) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.newContactTrigger = 'true';
        // The divider only earns its place under something; as the panel's
        // first child the trigger sits flush against the rounded top edge.
        const divider = leading ? '' : ' border-t border-gray-950/10';
        btn.className = 'block w-full text-left px-4 py-2 text-sm font-medium text-purple-700 bg-white' + divider + ' hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-600 cursor-pointer';
        btn.textContent = '+ Add new contact';
        // Bind directly — Stimulus 1.1 MutationObserver may not
        // pick up data-action on dynamically created elements.
        btn.addEventListener('click', () => this.showNewContact());
        return btn;
    }

    showNewContact() {
        const name = this.newContactNameFromSearch();

        // The payer the charge already has stays in the posted fields, and its
        // chip keeps its text -- only the chip is hidden, since the panel now
        // occupies that spot. Nothing here is a snapshot the browser has to
        // hold: a form the server re-renders after a failed save arrives in
        // this same state, so Cancel works there too. Whether the new contact
        // supersedes the payer is the server's call, made on the open panel.
        this.entitySelectedTarget.classList.add('hidden');
        this.hideEntityResults();

        this.setNewContactVisible(true);
        this.announceReplacement();
        this.newContactFirstNameTarget.value = name.first;
        this.newContactLastNameTarget.value = name.last;
        this.newContactLastNameTarget.focus();
    }

    cancelNewContact(e) {
        if (e) e.preventDefault();
        this.resetNewContact();
        this.showLinkedEntityChip();
        this.entitySearchTarget.focus();
    }

    // The chip the panel displaced is out of sight, so the panel has to say
    // where the payer went -- but only when there was one to displace.
    announceReplacement() {
        if (!this.hasNewContactReplacesTarget) return;

        this.newContactReplacesTarget.classList.toggle(
            'hidden',
            this.linkedEntityIdTarget.value === ''
        );
    }

    // Nothing to show when the charge has no payer -- it never had one, or the
    // user cleared it outright, which blanks the hidden field.
    showLinkedEntityChip() {
        if (this.linkedEntityIdTarget.value === '') return;

        this.entitySelectedTarget.classList.remove('hidden');
    }

    // A single word is a surname, not a given name: "Okonkwo" should land in
    // the field Contact actually requires.
    newContactNameFromSearch() {
        const query = this.entitySearchTarget.value.trim();
        if (query.length === 0) return {first: '', last: ''};

        const parts = query.split(/\s+/);
        if (parts.length === 1) return {first: '', last: parts[0]};
        return {first: parts[0], last: parts.slice(1).join(' ')};
    }

    resetNewContact() {
        if (!this.hasNewContactTarget) return;

        this.newContactFirstNameTarget.value = '';
        this.newContactLastNameTarget.value = '';
        this.setNewContactVisible(false);
    }

    // Hidden fields still post, so the panel's inputs are disabled whenever it
    // is closed — a name the user backed out of must not create a Contact.
    setNewContactVisible(visible) {
        if (!this.hasNewContactTarget) return;

        this.newContactTarget.classList.toggle('hidden', !visible);
        this.newContactFirstNameTarget.disabled = !visible;
        this.newContactLastNameTarget.disabled = !visible;
        // Posts only while the panel is open, which is how the server tells
        // "no contact wanted" from "panel open and both names emptied".
        if (this.hasNewContactOpenTarget) this.newContactOpenTarget.disabled = !visible;
    }

    entityDisplayName(type, item) {
        if (type === 'businesses') return item.business_name || '';
        if (type === 'contacts') return item.name_search_string || '';
        return item.name || String(item.id);
    }

    selectEntity(e) {
        const el = e.currentTarget;
        const id = el.dataset.entityId;
        const type = el.dataset.entityType;

        this.linkedEntityIdTarget.value = id;
        this.linkedEntityTypeTarget.value = type;

        this.entitySearchTarget.value = '';
        this.hideEntityResults();
        this.resetNewContact();

        this.entitySelectedTarget.classList.remove('hidden');
        this.entitySelectedNameTarget.textContent = el.textContent.trim();
        this.entitySelectedTypeTarget.textContent = '(' + type + ')';
    }

    clearEntity(e) {
        e.preventDefault();
        this.linkedEntityIdTarget.value = '';
        this.linkedEntityTypeTarget.value = '';
        this.entitySearchTarget.value = '';
        this.hideEntityResults();
        this.resetNewContact();
        this.entitySelectedTarget.classList.add('hidden');
        this.entitySelectedNameTarget.textContent = '';
        this.entitySelectedTypeTarget.textContent = '';
    }

    setFieldSetPostable(e) {
        this._updatePostReady();
    }

    deleteCharge(e) {
        e.preventDefault();
        var fieldset = e.target.closest('.misc-charge-fieldset');
        if (fieldset) {
            fieldset.remove();
            allChargesReady();
        }
    }

}
